/**
 * Reine Klassifikations-Logik (Vektor/Raster/Mischform) und Pre-Upload-Report-
 * Bauer für PDF-Pläne. Environment-agnostisch — nutzt keine Node-APIs und
 * keine pdfjs-Imports. Damit lädt das Frontend dieselbe Heuristik wie der
 * `pdf-classifier-agent` im Backend (Single Source of Truth).
 *
 * Wer pdfjs braucht, holt die Counts vorher selbst (Node: pdf-page-stats.ts,
 * Browser: lib/pdfClassify.ts) und ruft diese Funktionen mit den Roh-Zahlen.
 */

export const VECTOR_PATH_THRESHOLD = 200;
export const RASTER_IMAGE_THRESHOLD = 1;

export type RasterMode = "vector" | "raster" | "mixed";

export interface PageClassificationInput {
  pathConstructCount: number;
  imageStreamCount: number;
}

export function classifyPageMode(input: PageClassificationInput): RasterMode {
  const hasManyPaths = input.pathConstructCount > VECTOR_PATH_THRESHOLD;
  const hasImages = input.imageStreamCount >= RASTER_IMAGE_THRESHOLD;
  if (hasManyPaths && !hasImages) return "vector";
  if (!hasManyPaths && hasImages) return "raster";
  return "mixed";
}

export interface PreUploadPageReport {
  index: number;
  rasterMode: RasterMode;
  hasOcgLayers: boolean;
  pathConstructCount: number;
  imageStreamCount: number;
  pageWidth: number;
  pageHeight: number;
  warnings: string[];
}

export interface PreUploadReport {
  filename: string;
  numPages: number;
  pages: PreUploadPageReport[];
  hasAnyRasterPage: boolean;
  hasAnyVectorPage: boolean;
  detectedScaleHint: string | null;
  ocgLayerCount: number;
}

export interface BuildReportInput {
  filename: string;
  pageStats: Array<{
    index: number;
    pathConstructCount: number;
    imageStreamCount: number;
    pageWidth: number;
    pageHeight: number;
  }>;
  hasOcgLayers: boolean;
  ocgLayerCount: number;
  scaleHint: string | null;
}

export function buildPreUploadReport(input: BuildReportInput): PreUploadReport {
  const pages: PreUploadPageReport[] = input.pageStats.map((p) => {
    const mode = classifyPageMode(p);
    const warnings: string[] = [];
    if (mode === "raster") {
      warnings.push(
        "Raster-Seite ohne Vektor-Geometrie — Wände müssen manuell gezeichnet werden."
      );
      if (!input.hasOcgLayers) {
        warnings.push(
          "Keine OCG-Layer im Dokument — Wand-Maske kann nicht automatisch erstellt werden."
        );
      }
      if (!input.scaleHint) {
        warnings.push(
          "Maßstab nicht im Plan-Text gefunden — bitte manuell setzen."
        );
      }
    } else if (mode === "mixed") {
      warnings.push(
        "Mischform — Pipeline nutzt Vektor wenn vorhanden, sonst Raster-Branch."
      );
    } else if (mode === "vector" && !input.hasOcgLayers) {
      warnings.push(
        "Vektor-Geometrie ohne OCG-Layer — Wand-Maske via Floodfill möglich, aber Material-Detect entfällt."
      );
    }
    return {
      index: p.index,
      rasterMode: mode,
      hasOcgLayers: input.hasOcgLayers,
      pathConstructCount: p.pathConstructCount,
      imageStreamCount: p.imageStreamCount,
      pageWidth: p.pageWidth,
      pageHeight: p.pageHeight,
      warnings,
    };
  });

  return {
    filename: input.filename,
    numPages: pages.length,
    pages,
    hasAnyRasterPage: pages.some(
      (p) => p.rasterMode === "raster" || p.rasterMode === "mixed"
    ),
    hasAnyVectorPage: pages.some((p) => p.rasterMode === "vector"),
    detectedScaleHint: input.scaleHint,
    ocgLayerCount: input.ocgLayerCount,
  };
}

/**
 * Findet einen Maßstabs-Hint (z.B. "1:100", "M 1:50") im Plan-Text. Nimmt den
 * ersten plausiblen Match. Filtert Datums-/Versions-Strings ("1:1" als Verhältnis
 * ist erlaubt, aber keine 5-stelligen Nenner).
 */
export function extractScaleHintFromText(text: string): string | null {
  const match = text.match(/\b1\s*:\s*(\d{1,4})\b/);
  if (!match) return null;
  const denominator = Number(match[1]);
  if (denominator < 1 || denominator > 5000) return null;
  return `1:${denominator}`;
}
